import { IDateRange } from '@linearb/linjs-common';
import {
  FormControlLabel,
  Radio,
  RadioGroup
} from '@linearb/linui-common/base';
import { SEARCH_DATE_FORMAT } from '@linearb/linui-utils';
import { Stack } from '@mui/material';
import { ObjectValues } from 'common/utils/objectValues';
import RangePicker from 'components/base/RangePicker';
import startCase from 'lodash/startCase';
import moment, { type Moment } from 'moment';

export const DATE_FILTERS = {
  UPDATED: 'updated',
  CREATED: 'created',
  DEPLOYED: 'deployed',
  CODING_ENDED: 'coding_ended',
  REVIEWED: 'reviewed',
  MERGED: 'merged',
  RELEASED: 'released',
  RELEASED_AT: 'released_at',
  CYCLE_TIME_UPDATED: 'cycle_time_updated',
  COMMITTED: 'committed',
  ISSUED_AT: 'issued_at',
  IN_PROGRESS_BETWEEN: 'in_progress_between',
  STARTED_AT: 'started_at',
  ENDED_AT: 'ended_at',
  // Used in team activity page
  TIME: 'time'
} as const;

export type DateFilter = ObjectValues<typeof DATE_FILTERS>;

export interface DateFilterValue {
  filterName: DateFilter;
  range: IDateRange;
}

interface DateFilterProps {
  range: IDateRange;
  selectedDateFilter: DateFilter;
  /**
   * List of date filters to render
   */
  filters: DateFilter[];
  filtersConfig: Record<string, any>;
  onChange(oldValue: DateFilterValue, newValue: DateFilterValue): void;
}

/**
 * Date filter component that allows selecting the date range and type of filter
 * Assumes only one type of filter can be picked at any time
 */
export function DateFilter({
  range,
  selectedDateFilter,
  onChange,
  filters = [],
  filtersConfig
}: DateFilterProps) {
  const { from, to } = range;
  const minDate: Moment | undefined =
    filtersConfig?.[selectedDateFilter]?.minDate();

  function handleSelectedDateFilterChange(_event: never, value: DateFilter) {
    onChange(
      { range, filterName: selectedDateFilter as DateFilter },
      {
        range,
        filterName: value
      }
    );
  }

  function handleDateRangeChange(newRange: IDateRange) {
    onChange(
      { range, filterName: selectedDateFilter as DateFilter },
      { range: newRange, filterName: selectedDateFilter as DateFilter }
    );
  }

  return (
    <Stack gap={1} marginTop={2} marginX={2}>
      <RangePicker
        name={selectedDateFilter}
        height='2.1rem'
        initialVisibleMonth={() => moment().subtract(1, 'month')}
        startDate={from ? moment(from) : null}
        endDate={to ? moment(to) : null}
        enableOutsideDays={false}
        onDatesChange={event => {
          const { endDate: to, startDate: from } = event;

          handleDateRangeChange({
            to: (to as Moment)?.format(SEARCH_DATE_FORMAT),
            from: (from as Moment)?.format(SEARCH_DATE_FORMAT)
          });
        }}
        isOutsideRange={(date: undefined | Moment) => {
          if (date?.isAfter(moment(), 'day')) {
            return true;
          }

          return date?.isBefore(minDate, 'day');
        }}
      />
      <RadioGroup
        sx={{ paddingX: 2 }}
        onChange={handleSelectedDateFilterChange}
      >
        {filters.map(filter => (
          <FormControlLabel
            control={<Radio checked={filter === selectedDateFilter} />}
            label={startCase(filter)}
            value={filter}
            key={filter}
          />
        ))}
      </RadioGroup>
    </Stack>
  );
}
